import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { enrollmentTransferApi, type TransferRollbackResult } from '@/api/enrollmentTransfer';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { errorRows, transferErrorKey, transferTableLabel } from '@/lib/enrollmentTransfer';

/**
 * Журнал переносов ученика с «Откатить» (договор `enrollment-transfer.md` §9). Откатить можно только последний
 * неотменённый перенос (`can_rollback`): откат A→B после B→C утащил бы строки из C.
 *
 * Пустой журнал не рисуется вовсе — у большинства учеников переносов нет, и пустая карточка только занимала бы место.
 */
export function TransferJournal({ studentId }: { studentId: string }) {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [result, setResult] = useState<TransferRollbackResult | null>(null);

  const journal = useQuery({
    queryKey: ['admin', 'transfers', studentId],
    queryFn: () => enrollmentTransferApi.list(studentId),
  });
  const rollback = useMutation({
    mutationFn: (id: string) => enrollmentTransferApi.rollback(id),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['admin', 'transfers', studentId] });
      qc.invalidateQueries({ queryKey: ['admin', 'student', studentId] });
      qc.invalidateQueries({ queryKey: ['progress', 'student-points', studentId] });
    },
  });

  const rows = journal.data ?? [];
  if (rows.length === 0 && !journal.error) return null;
  const blocking = errorRows(rollback.error);

  return (
    <Card className="mb-4">
      <CardTitle>{t('adminTransfer.journalTitle')}</CardTitle>
      {journal.error ? (
        <p className="mt-3 text-sm text-error">{t(transferErrorKey(journal.error))}</p>
      ) : (
        <ul className="mt-3 space-y-2 text-sm">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-card bg-surface-container-highest p-3">
              <div>
                <p>
                  {r.from_title ?? r.from_marathon_id.slice(0, 8)} → {r.to_title ?? r.to_marathon_id.slice(0, 8)}
                </p>
                <p className="text-xs text-on-surface-variant">
                  {new Date(r.created_at).toLocaleString('ru-RU')}
                  {r.verify && ` · ${r.verify.ok ? t('adminTransfer.verifyOkShort') : t('adminTransfer.verifyBadShort')}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {r.status === 'rolled_back' ? (
                  <Pill>
                    {t('adminTransfer.rolledBack', {
                      date: r.rolled_back_at ? new Date(r.rolled_back_at).toLocaleString('ru-RU') : '—',
                    })}
                  </Pill>
                ) : (
                  <Pill variant="success">{t('adminTransfer.statusDone')}</Pill>
                )}
                {r.can_rollback && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={rollback.isPending && rollback.variables === r.id}
                    onClick={async () => {
                      const msg = t('adminTransfer.confirmRollback', {
                        from: r.from_title ?? '',
                        to: r.to_title ?? '',
                      });
                      if (await ask({ message: msg, danger: true })) {
                        setResult(null);
                        rollback.mutate(r.id);
                      }
                    }}
                  >
                    {t('adminTransfer.rollback')}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {result && (
        <p className="mt-3 text-sm text-success">
          {t('adminTransfer.rollbackDone', { n: result.restored.reduce((s, x) => s + x.rows, 0) })}
        </p>
      )}
      {rollback.error != null && (
        <div className="mt-3 text-sm text-error">
          <p>{t(transferErrorKey(rollback.error))}</p>
          {blocking.map((b) => (
            <p key={b.table} className="text-xs">
              {transferTableLabel(b.table, t)}: {b.rows}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}
